import i18n from 'i18n-js';
import * as Yup from 'yup';

const initialValues = ({ id, ruleset, orgType, allowFullEdit, paymentsEnabled, multipleBufferEnabled }) => {
  if (allowFullEdit) {
    parseRuleset(ruleset);
  }
  const cleanPayload = {
    name: '',
    description: '',
    enabled: true,
    //Customer (driver_rules) are show only for services
    ...(orgType === 'msServiceOrg'
      ? {
          driver_rules: {
            reservation_type: 'DRIVER',
            duration_min: {
              magnitude: '1',
              units: 'hour',
            },
            duration_max: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_early_start: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_early_end: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_end_margin_buffer: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_end_service_buffer: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_reserve_ahead_min: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_reserve_ahead_max: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_vehicle_confirmed: {
              magnitude: '1',
              units: 'hour',
            },
            duration_min_cancelled: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_times_locked: {
              magnitude: '1',
              units: 'hour',
            },
            concurrent_max: '1',
            timedelta_create_grace_period: {
              magnitude: '1',
              units: 'hour',
            },
            timedelta_max_late: {
              magnitude: '1',
              units: 'hour',
            },
            start_time_resolution: {
              magnitude: '1',
              units: 'hour',
            },
            end_time_resolution: {
              magnitude: '1',
              units: 'hour',
            },
            duration_default: {
              magnitude: '1',
              units: 'hour',
            },
            duration_adjust_end: {
              magnitude: '1',
              units: 'hour',
            },
            ...(multipleBufferEnabled
              ? {
                  multiple_maintenance_buffer: [
                  {
                    time_range: null,
                    buffer: {
                      magnitude: '72',
                      units: 'hour',
                    },
                    is_default: true,
                  },
                  ],
                }
              : {}),
          },
          ...(paymentsEnabled !== false
            ? {
                payment_framework: 'TRADITIONAL',
                payment_framework_settings: {
                  display_info: {
                    at_creation: { disclaimer: '' },
                    at_start: { disclaimer: '' },
                    at_end: { disclaimer: '' },
                    at_cancel: { disclaimer: '' },
                    upon_modify: { disclaimer: '' },
                  },
                  charge_period: '0 days',
                  payment_grace_period: '0 days',
                  pre_auth_factor: '1',
                  pre_auth_offset: '0',
                  pre_auth_request: true,
                },
              }
            : {}),
        }
      : {}),
    offline_rules: {
      reservation_type: 'OFFLINE',
      duration_min: {
        magnitude: '1',
        units: 'hour',
      },
      duration_max: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_early_start: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_early_end: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_end_margin_buffer: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_end_service_buffer: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_reserve_ahead_min: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_reserve_ahead_max: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_vehicle_confirmed: {
        magnitude: '1',
        units: 'hour',
      },
      duration_min_cancelled: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_times_locked: {
        magnitude: '1',
        units: 'hour',
      },
      concurrent_max: '1',
      timedelta_create_grace_period: {
        magnitude: '1',
        units: 'hour',
      },
      timedelta_max_late: {
        magnitude: '1',
        units: 'hour',
      },
      start_time_resolution: {
        magnitude: '1',
        units: 'hour',
      },
      end_time_resolution: {
        magnitude: '1',
        units: 'hour',
      },
      duration_default: {
        magnitude: '1',
        units: 'hour',
      },
      duration_adjust_end: {
        magnitude: '1',
        units: 'hour',
      },
    },
    view_order: null,
    ...(!isNaN(id)
      ? {
          ...ruleset,
          ...(orgType === 'msServiceOrg'
            ? {
                driver_rules: {
                  ...ruleset.driver_rules,
                  multiple_maintenance_buffer: getInitialBuffer(ruleset),
                },
              }
            : {}),
        }
      : {}),
  };
  return cleanPayload;
};

const validationValues = {
  min: [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30],
  hr: [1, 2, 3, 4, 6, 12],
};

const magnitudeSchema = () =>
  Yup.object().shape({
    magnitude: Yup.number()
      .required(i18n.t('common.required_field'))
      .typeError(i18n.t('common.type_error')),
    units: Yup.string().required(i18n.t('common.required_field')),
  });

const endResolutionMagnitudeSchema = () =>
  Yup.object().shape({
    magnitude: Yup.number()
      .when('units', {
        is: 'min',
        then: Yup.number()
          .oneOf(
            validationValues.min,
            i18n.t('common.invalid_magnitude', { values: validationValues.min.join(', ') })
          )
          .required(i18n.t('common.required_field')),
        otherwise: Yup.number().when('units', {
          is: 'hr',
          then: Yup.number()
            .oneOf(
              validationValues.hr,
              i18n.t('common.invalid_magnitude', { values: validationValues.hr.join(', ') })
            )
            .required(i18n.t('common.required_field')),
          otherwise: Yup.number()
            .required(i18n.t('common.required_field'))
            .typeError(i18n.t('common.type_error')),
        }),
      })
      .required(i18n.t('common.required_field'))
      .typeError(i18n.t('common.type_error')),
    units: Yup.string().required(i18n.t('common.required_field')),
  });

const paymentFrameworkSchema = () =>
  Yup.object().shape({
    display_info: Yup.object().shape({
      at_creation: Yup.object().shape({
        disclaimer: Yup.string().nullable(),
        process_type: Yup.string().nullable(),
      }),
      at_start: Yup.object().shape({
        disclaimer: Yup.string().nullable(),
        process_type: Yup.string().nullable(),
      }),
      at_end: Yup.object().shape({
        disclaimer: Yup.string().nullable(),
        process_type: Yup.string().nullable(),
      }),
      at_cancel: Yup.object().shape({
        disclaimer: Yup.string().nullable(),
        process_type: Yup.string().nullable(),
      }),
      upon_modify: Yup.object().shape({
        disclaimer: Yup.string().nullable(),
        process_type: Yup.string().nullable(),
      }),
    }),
    charge_period: Yup.string().required(i18n.t('common.required_field')),
    payment_grace_period: Yup.string().required(i18n.t('common.required_field')),
    pre_auth_factor: Yup.number()
      .required(i18n.t('common.required_field'))
      .typeError(i18n.t('common.type_error')),
    pre_auth_offset: Yup.number()
      .required(i18n.t('common.required_field'))
      .typeError(i18n.t('common.type_error')),
    pre_auth_request: Yup.boolean().required(i18n.t('common.required_field')),
    payment_framework_info: Yup.object().notRequired(),
  });

const formSchema = (orgType, paymentsEnabled) =>
  Yup.object().shape({
    id: Yup.number().notRequired(),
    name: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string().nullable(),
    display_name: Yup.string().notRequired(),
    enabled: Yup.boolean(),
    archived: Yup.date().nullable(),
    created_time: Yup.date().notRequired(),
    view_order: Yup.number().nullable().min(1).typeError(i18n.t('common.type_error')),
    //Customer (driver_rules) are show only for services
    ...(orgType === 'msServiceOrg'
      ? {
          driver_rules: Yup.object().shape({
            reservation_type: Yup.string(),
            duration_min: magnitudeSchema(),
            duration_max: magnitudeSchema(),
            timedelta_early_start: magnitudeSchema(),
            timedelta_early_end: magnitudeSchema(),
            timedelta_end_margin_buffer: magnitudeSchema(),
            timedelta_reserve_ahead_min: magnitudeSchema(),
            timedelta_reserve_ahead_max: magnitudeSchema(),
            timedelta_vehicle_confirmed: magnitudeSchema(),
            duration_min_cancelled: magnitudeSchema(),
            timedelta_times_locked: magnitudeSchema(),
            concurrent_max: Yup.number().required(i18n.t('common.required_field')),
            timedelta_create_grace_period: magnitudeSchema(),
            timedelta_max_late: magnitudeSchema(),
            start_time_resolution: magnitudeSchema(),
            end_time_resolution: endResolutionMagnitudeSchema(),
            duration_default: magnitudeSchema(),
            duration_adjust_end: magnitudeSchema(),
          }),
          ...(paymentsEnabled !== false
            ? {
                payment_framework: Yup.string().required(i18n.t('common.required_field')),
                payment_framework_settings: paymentFrameworkSchema(),
              }
            : {}),
        }
      : {}),
    offline_rules: Yup.object().shape({
      reservation_type: Yup.string(),
      duration_min: magnitudeSchema(),
      duration_max: magnitudeSchema(),
      timedelta_early_start: magnitudeSchema(),
      timedelta_early_end: magnitudeSchema(),
      timedelta_end_margin_buffer: magnitudeSchema(),
      timedelta_end_service_buffer: magnitudeSchema(),
      timedelta_reserve_ahead_min: magnitudeSchema(),
      timedelta_reserve_ahead_max: magnitudeSchema(),
      timedelta_vehicle_confirmed: magnitudeSchema(),
      duration_min_cancelled: magnitudeSchema(),
      timedelta_times_locked: magnitudeSchema(),
      concurrent_max: Yup.number().required(i18n.t('common.required_field')),
      timedelta_create_grace_period: magnitudeSchema(),
      timedelta_max_late: magnitudeSchema(),
      start_time_resolution: magnitudeSchema(),
      end_time_resolution: endResolutionMagnitudeSchema(),
      duration_default: magnitudeSchema(),
      duration_adjust_end: magnitudeSchema(),
    }),
  });

/**
 * Replace null values in a object with empty string, parse both magnitude and view_order
 *
 * @param {*} ruleset
 */
export const parseRuleset = (ruleset) => {
  for (let key in ruleset) {
    ruleset[key] = ruleset[key] === null && key != 'time_range' ? '' : ruleset[key];
    if (
      key === 'magnitude' ||
      key === 'view_order' ||
      key === 'pre_auth_factor' ||
      key === 'pre_auth_offset'
    ) {
      ruleset[key] = `${ruleset[key]}`;
    }
    if (ruleset[key] !== null && typeof ruleset[key] == 'object') {
      parseRuleset(ruleset[key]);
    }
  }
};

export const getInitialBuffer = (ruleset) => {
  const usesVariableBuffer =
    Array.isArray(ruleset?.driver_rules?.multiple_maintenance_buffer) &&
    ruleset?.driver_rules?.multiple_maintenance_buffer?.length > 0;
  const newBuffer = usesVariableBuffer
    ? [...ruleset?.driver_rules?.multiple_maintenance_buffer]
    : [
        {
          time_range: null,
          buffer: ruleset?.driver_rules?.timedelta_end_service_buffer
            ? { ...ruleset?.driver_rules?.timedelta_end_service_buffer }
            : {
                magnitude: '1',
                units: 'hour',
              },
          is_default: true,
        },
      ];
  return newBuffer;
};

export { initialValues, magnitudeSchema, paymentFrameworkSchema, formSchema };
